/**
 * Per-tenant theme application for Survey Says.
 *
 * Internal-to-survey-says copy of the WhatIs theme module. The CSS-var names
 * are kept identical (--sg-*) so streamers who already tuned their WhatIs
 * theme see the same colors here automatically.
 */
import type { ThemePalette } from '@sg/shared';

export const THEME_VARS = {
  primary: '--sg-primary',
  secondary: '--sg-secondary',
  accent: '--sg-accent',
  background: '--sg-bg',
  text_on_primary: '--sg-text-on-primary',
  text_on_secondary: '--sg-text-on-secondary',
  tile_bg: '--sg-tile-bg',
  celebration: '--sg-celebration',
} as const;

/**
 * Survey Says default palette. Slight twist on WhatIs: a deeper magenta accent
 * to give the game its own visual identity while keeping the brand yellow +
 * deep blue. Streamer-customisable via the dashboard.
 */
export const DEFAULT_THEME: ThemePalette = {
  primary: '#FFC43D',
  secondary: '#060CE9',
  accent: '#E91E63',
  background: '#0A0E27',
  text_on_primary: '#060CE9',
  text_on_secondary: '#FFC43D',
  tile_bg: '#060CE9',
  celebration: '#22C55E',
};

export function applyTheme(theme: ThemePalette): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty(THEME_VARS.primary, theme.primary);
  root.style.setProperty(THEME_VARS.secondary, theme.secondary);
  root.style.setProperty(THEME_VARS.accent, theme.accent);
  root.style.setProperty(THEME_VARS.background, theme.background);
  root.style.setProperty(THEME_VARS.text_on_primary, theme.text_on_primary);
  root.style.setProperty(THEME_VARS.text_on_secondary, theme.text_on_secondary);
  root.style.setProperty(THEME_VARS.tile_bg, theme.tile_bg);
  root.style.setProperty(THEME_VARS.celebration, theme.celebration);
}

/**
 * Read a theme CSS custom property as a `#RRGGBB` string. Used by the canvas
 * sparkles system that can't consume CSS vars natively. Falls back to
 * `fallback` when the var is unset or malformed.
 */
export function getThemeVar(
  name: keyof typeof THEME_VARS,
  fallback: string,
): string {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') {
    return fallback;
  }
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(THEME_VARS[name])
    .trim();
  return /^#[0-9A-Fa-f]{6}$/.test(raw) ? raw : fallback;
}
